import { useQueryGetAllBots } from "@apis:bot/bot";
import { useQueryGetAllOrganizations } from "@apis:organization/organization";
import { useQueryGetAllUiPreferences } from "@apis:ui_preferences/ui_preferences";
import { LoadingScreen } from "@components:LoadingScreen";
import { useUser } from "@libs:react-query-auth";
import { ReactNode } from "react";
import { Navigate, Outlet } from "react-router";

import { AppType } from "@:constants";

interface BotGuardedRouteProps {
  appType: AppType;
  fallbackRoute?: string;
  children?: ReactNode;
}

/**
 * Route guard that ensures bots are available for the specified app type
 * Displays loading screen during bot, organization and preferences fetch
 * Prefetches organizations and preferences to eliminate BotSelection visual flicker
 * Redirects to fallback if no bots available
 * Matches ProtectedRoute/PermissionRoute pattern
 */
export const BotGuardedRoute = ({
  appType,
  fallbackRoute = "/app",
  children,
}: BotGuardedRouteProps) => {
  const user = useUser().data;
  const userId = user?.id;
  const { data: bots, isLoading: isLoadingBots } = useQueryGetAllBots(
    true,
    appType,
  );
  const { isLoading: isLoadingOrgs } = useQueryGetAllOrganizations();

  const { isLoading } = useQueryGetAllUiPreferences(true, userId, undefined, {
    enabled: !!userId,
  });

  // Show loading during fetch (bots, organizations and preferences)
  if (isLoadingBots || isLoadingOrgs || isLoading) {
    return <LoadingScreen />;
  }

  // Redirect if no bots available
  if (!bots || bots.length === 0) {
    return <Navigate to={fallbackRoute} replace />;
  }

  // Render children or outlet
  return children ? <>{children}</> : <Outlet />;
};
